// Homepage preloader — orchestration controller.
//
// All motion lives in CSS (src/styles/preloader.scss). This module just:
//   1. Mounts the dotLottie player into `.sentiments-preloader__mark`.
//   2. Toggles state classes on the root:
//        (mounted)              → Lottie loops continuously
//        + .is-revealing        → mark fades in to visible scale
//        + .is-exiting          → mark fades, both curtain halves part
//   3. Removes the node from the DOM at the end.
//
// Exit policy (all three must be true):
//   - window.load has fired (homepage hero + above-the-fold assets ready)
//   - ≥ MIN_VISIBLE_MS elapsed since mount (so the loop reads)
//   - Lottie player has reported `load` OR the safety timeout has tripped
//
// The safety timeout is the key reliability fix: on slow networks the
// dotLottie chunk + the .lottie file together can take longer than
// MIN_VISIBLE_MS to arrive. Without the gate, the preloader exited
// before the animation ever painted (user-reported: "logo doesn't even
// show"). With the gate, the curtain holds until either Lottie renders
// OR LOTTIE_SAFETY_MS (a hard ceiling) elapses, whichever comes first.
//
// Scope: bails if no `.sentiments-preloader` is in the DOM. PHP only
// renders the markup on the actual homepage (is_front_page()), so this
// module is automatically a no-op everywhere else.

const ASSET_URL = '/wp-content/themes/sentiments/assets/logo/eye-animation.lottie';

// Timing knobs. MIN_VISIBLE_MS = 3 s gives the lottie loop room to
// breathe; with the asset preloaded via <link rel="preload"> (see
// sentiments_preload_lottie_asset in functions.php) the player is
// usually ready well before this window closes.
const MIN_VISIBLE_MS    = 3000;
const LOTTIE_SAFETY_MS  = 5000; // hard ceiling — never wait longer than this
                                 // for Lottie before letting the preloader exit
const EXIT_TO_REMOVE    = 1700; // mark-fade 0.7s + curtain-rise 0.95s + safety

export function initPreloader() {
	const root = document.querySelector('.sentiments-preloader');
	if (!root) return;

	const mark = root.querySelector('.sentiments-preloader__mark');
	if (!mark) {
		teardown(root);
		return;
	}

	// Lock page scroll while the preloader is up.
	const prevHtmlOverflow = document.documentElement.style.overflow;
	const prevBodyOverflow = document.body.style.overflow;
	document.documentElement.style.overflow = 'hidden';
	document.body.style.overflow = 'hidden';

	const restoreScroll = () => {
		document.documentElement.style.overflow = prevHtmlOverflow;
		document.body.style.overflow = prevBodyOverflow;
	};

	const startTime = performance.now();

	// `lottieReady` resolves when the player reports the .lottie file is
	// parsed and the first frame is ready to paint, OR when the safety
	// timeout fires — whichever happens first. The exit logic awaits this
	// before tearing down so users on slow networks still see the loop.
	const lottieReady = new Promise((resolve) => {
		const safety = setTimeout(() => resolve('timeout'), LOTTIE_SAFETY_MS);

		// ── Mount Lottie (dynamic import — only fetched on homepage) ──
		import('@lottiefiles/dotlottie-web')
			.then(({ DotLottie }) => {
				const canvas = document.createElement('canvas');
				canvas.style.width  = '100%';
				canvas.style.height = '100%';
				canvas.style.display = 'block';
				mark.appendChild(canvas);

				const player = new DotLottie({
					canvas,
					src: ASSET_URL,
					loop: true,         // continuous loop while preloader is up
					autoplay: true,
					// `bounce` mode plays the animation forward then reverse
					// instead of snapping back to frame 0 on each loop. The
					// source eye-mark .lottie has a 9-keyframe shape-path
					// morph that runs from frame 0 to ~127, but the
					// composition's out-point is set to frame 68 — so a
					// forward-only loop cuts off the morph at 53 % and snaps
					// back to the start each iteration, producing the
					// "blinks empty between loops" flicker users reported.
					// Bounce plays 0 → 68 → 0 → 68 …, hitting the same
					// visible state at both ends of every cycle, so the
					// boundary is invisible and the mark always reads as
					// continuously moving.
					mode: 'bounce',
					renderConfig: { autoResize: true },
				});

				// dotLottie fires `load` once the animation data is parsed
				// and the first frame is queued. That's our signal that the
				// user is actually about to see motion.
				player.addEventListener('load', () => {
					clearTimeout(safety);
					resolve('ready');
				});
				// If parsing fails we still want to exit cleanly — resolve so
				// the preloader doesn't hang forever on a broken asset.
				player.addEventListener('loadError', (err) => {
					console.warn('[sentiments] preloader: lottie loadError', err);
					clearTimeout(safety);
					resolve('error');
				});
			})
			.catch((err) => {
				// dotLottie chunk failed to fetch — typical on offline or
				// CORS-blocked environments. Fall through to the curtain
				// reveal without animation; safety timeout already covers us.
				console.warn('[sentiments] preloader: dotLottie chunk load failed', err);
				clearTimeout(safety);
				resolve('chunk-failed');
			});
	});

	// ── Kick off the reveal on the next animation frame ─────────────
	//    Wait one frame so the browser has the initial styles applied
	//    before we trigger the transitions — otherwise the at-rest
	//    state can be skipped (no animation visible).
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			root.classList.add('is-revealing');
		});
	});

	// ── Schedule the exit ───────────────────────────────────────────
	//    Wait for: window.load, MIN_VISIBLE_MS, lottieReady — all three.
	const triggerExit = () => {
		root.classList.remove('is-revealing');
		root.classList.add('is-exiting');
		setTimeout(() => {
			restoreScroll();
			teardown(root);
		}, EXIT_TO_REMOVE);
	};

	const allReady = async () => {
		await lottieReady;
		const elapsed = performance.now() - startTime;
		const waitMore = Math.max(0, MIN_VISIBLE_MS - elapsed);
		setTimeout(triggerExit, waitMore);
	};

	if (document.readyState === 'complete') {
		allReady();
	} else {
		window.addEventListener('load', allReady, { once: true });
	}
}

function teardown(root) {
	root.setAttribute('data-preloader-state', 'done');
	requestAnimationFrame(() => {
		root.remove();
	});
}
